/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ActionCards, URGENCY_STATES } from '../actionCards/ActionCards';
import { STAGE_ACCENT_COLORS, CHIP_RED, CHIP_GREEN } from '../actionCards/actionCardColors';
import { buildTotalChip } from '../actionCards/totalChip';
import { resolveDueNowCounts, resolveNewLeadTotal, resolveNoActiveTaskBucket, resolveNoActiveTaskCount } from '../../utils/newLeadDueNowCompat';
import { urgencyColor } from '../tableShell/activeWidget';
import { STAGE_ICONS } from '../../constants/stageIcons';
import { useSelector, dispatch } from '../../redux/store';
import {
    getNewLeadTaskWidgetCounts,
    clearNewLeadTaskCounts,
} from '../../redux/slices/widgetCounter';

// Bucket keys matching BE response fields and the task_due_bucket query param.
const BUCKET = {
    OVERDUE: 'overdue',
    DUE_TODAY: 'due_today',
    // Task N: real BE bucket = overdue ∪ due_today (task_due_bucket=due_now
    // is accepted server-side). Until the BE ships `due_now` in the counts
    // response, fall back to overdue+due_today for the headline — see
    // dueNowCount below. Remove the fallback once the BE lands on staging.
    DUE_NOW: 'due_now',
    UPCOMING: 'upcoming',
    CHECKLIST_COMPLETE: 'checklist_complete',
    NO_NEXT_ACTION: 'no_next_action',
    // Union of checklist_complete + no_next_action — only on a backend that returns it
    // (see utils/newLeadDueNowCompat.resolveNoActiveTaskBucket).
    NO_ACTIVE_TASK: 'no_active_task',
};

const BUCKET_LABELS = {
    [BUCKET.OVERDUE]: 'Overdue',
    [BUCKET.DUE_TODAY]: 'Due today',
    [BUCKET.DUE_NOW]: 'Tasks due',
    [BUCKET.UPCOMING]: 'Upcoming',
    [BUCKET.CHECKLIST_COMPLETE]: 'Checklist complete',
    [BUCKET.NO_NEXT_ACTION]: 'No tasks',
};

const BUCKET_ICONS = {
    [BUCKET.DUE_NOW]: STAGE_ICONS.NEW,
    [BUCKET.UPCOMING]: 'mdi:clock-outline',
};

const BUCKET_TOOLTIPS = {
    [BUCKET.DUE_NOW]: "New leads with an active task that's overdue or due today.",
    [BUCKET.UPCOMING]: 'New leads with an active task scheduled for a future date.',
};

const NO_ACTIVE_TASK_TOOLTIP = 'New leads with no active task — either their checklist is complete, or they have nothing scheduled.';

// Urgency colours follow the same conventions as AttendedTabContextualWidget:
// overdue / no-next-action are red/urgent, due-today is amber/warning,
// upcoming with tasks is neutral-good, no tasks is also urgent.
//
// Task O: the "Tasks due" CARD's own headline/left-bar no longer turns red — it
// takes a fixed blue stage accent instead (see `accentColor` on the due_now card
// below), regardless of count. The OVERDUE branch below is still used for the
// TableShell active-filter trace colour (onActiveWidgetChange, not this card's own
// headline) — RED stays reserved for the "Overdue" bucket there too.
const getUrgency = (bucket, count) => {
    if (bucket === BUCKET.DUE_NOW) {
        return URGENCY_STATES.NEUTRAL;
    }
    if (bucket === BUCKET.OVERDUE) {
        return count > 0 ? URGENCY_STATES.URGENT : URGENCY_STATES.NEUTRAL;
    }
    if (bucket === BUCKET.NO_NEXT_ACTION) {
        return count > 0 ? URGENCY_STATES.URGENT : URGENCY_STATES.GOOD;
    }
    if (bucket === BUCKET.DUE_TODAY) {
        return count > 0 ? URGENCY_STATES.WARNING : URGENCY_STATES.NEUTRAL;
    }
    if (bucket === BUCKET.CHECKLIST_COMPLETE) {
        // Always a positive/done state — these leads finished their journey.
        return URGENCY_STATES.GOOD;
    }
    // Upcoming
    return URGENCY_STATES.NEUTRAL;
};

// -----------------------------------------------------------------------------
// Task N / Task K: two "merged card" families share this widget — a card whose
// body selects one bucket and whose two chips (isV2Split's generic splitChips)
// refine it to one of two narrower buckets:
//   - "Tasks due" (due_now): body -> due_now (the union); chips -> overdue /
//     due_today (both DISJOINT from each other and distinct from due_now).
//   - "No active task": body -> no_next_action (there's no real combined
//     bucket here — no_next_action IS one of the two chip buckets itself);
//     chips -> checklist_complete / no_next_action.
// `cardKey` is what identifies the card to ActionCards (selectedKey/loadedKeys/
// click routing) — a REAL bucket value for "due_now" (it's a real BE bucket),
// a UI-only key for "no_active_task" (no combined bucket exists there).
// `bodyBucket` is the REAL bucket onBucketSelect receives for a body click.
// -----------------------------------------------------------------------------
const MERGED_FAMILIES = [
    {
        cardKey: BUCKET.DUE_NOW,
        cardLabel: BUCKET_LABELS[BUCKET.DUE_NOW],
        bodyBucket: BUCKET.DUE_NOW,
        // Task N spec: "active chip again -> due_now" — the chip is a
        // refinement of a REAL combined bucket, so toggling it off falls
        // back to that combined view rather than clearing entirely.
        chipFallback: 'body',
        // Task O: RED reserved for "Overdue" only — "Due today" is GREEN (on-track
        // tokens), matching the other "due now" chip rows across the app.
        chips: [
            { bucket: BUCKET.OVERDUE, label: BUCKET_LABELS[BUCKET.OVERDUE], type: 'overdue', dotColor: CHIP_RED.text },
            { bucket: BUCKET.DUE_TODAY, label: BUCKET_LABELS[BUCKET.DUE_TODAY], type: 'on-track', dotColor: CHIP_GREEN.text },
        ],
    },
    {
        cardKey: 'no_active_task',
        cardLabel: 'No active task',
        bodyBucket: BUCKET.NO_NEXT_ACTION, // OLD-backend body; a NEW backend uses the union bucket
        extraBodyBuckets: [BUCKET.NO_ACTIVE_TASK],
        // Task K behaviour, unchanged: there's no real "combined" bucket to
        // fall back to here (no_next_action IS one of the two chips itself),
        // so toggling off the active chip clears entirely.
        chipFallback: 'null',
        // Task O (part 2): RED is reserved for "Overdue" only — "No tasks" is not an
        // Overdue chip, so it's now amber (due-today tokens), not red.
        chips: [
            { bucket: BUCKET.CHECKLIST_COMPLETE, label: BUCKET_LABELS[BUCKET.CHECKLIST_COMPLETE], type: 'on-track', dotColor: CHIP_GREEN.text },
            { bucket: BUCKET.NO_NEXT_ACTION, label: BUCKET_LABELS[BUCKET.NO_NEXT_ACTION], type: 'due-today', dotColor: '#d97706' },
        ],
    },
];

// Is `key` either this family's body bucket or one of its two chip buckets?
const isFamilyActive = (key, family) => (
    key === family.bodyBucket
    || (family.extraBodyBuckets || []).includes(key)
    || family.chips.some((c) => c.bucket === key)
);

// Resolves the next bucket for a click on a merged-family card/chip, given the
// CURRENTLY active key. `isBodyClick` must be passed explicitly rather than
// inferred from `clickedBucket === family.bodyBucket` — for the "No active
// task" family, the "No tasks" CHIP's own bucket (no_next_action) IS the
// family's body bucket, so value-equality alone can't tell a chip click on
// that particular chip apart from an actual card-body click.
//
// Body click toggles the whole family off (-> null) if any part of it is
// active, otherwise selects the body bucket. Chip click selects that chip's
// bucket, unless it's already active — then it falls back per the family's
// own `chipFallback` ('body' or 'null', see MERGED_FAMILIES above).
const resolveFamilyClickTarget = (clickedBucket, currentKey, family, isBodyClick, bodyBucket = family.bodyBucket) => {
    if (isBodyClick) {
        return isFamilyActive(currentKey, family) ? null : bodyBucket;
    }
    if (currentKey === clickedBucket) {
        return family.chipFallback === 'body' ? bodyBucket : null;
    }
    return clickedBucket;
};

// The single ActionCards `selectedKey` this tracedKey maps to — a family's
// `cardKey` if tracedKey belongs to that family, otherwise tracedKey itself
// (a plain card, e.g. 'upcoming').
const resolveSelectedCardKey = (tracedKey) => {
    if (!tracedKey) return null;
    const family = MERGED_FAMILIES.find((f) => isFamilyActive(tracedKey, f));
    return family ? family.cardKey : tracedKey;
};

// Label for the unscoped (no bucket) list — shown on the strip trace and the total badge.
const ALL_NEW_LABEL = 'All new leads';

// ------------------------------------------------------------------
// NewLeadTaskDueWidget
//
// Renders task bucket cards ("Tasks due" [due_now, merging Overdue + Due
// Today behind two chips — Task N] / Upcoming / "No active task" [merging
// Checklist Complete + No Tasks behind two chips — Task K]) for the New Lead
// table when it's in "Tasks" view mode.
// Only mounted when hasFeatureAccess('tasks-v2') is true — the parent
// (NewLead.jsx) gates the render entirely.
//
// Clicking a card calls onBucketSelect(bucketKey) to let the parent
// add task_due_bucket to the getNewLead query. Clicking the active
// card again clears the filter (toggle behaviour).
// ------------------------------------------------------------------
const NewLeadTaskDueWidget = ({
    practiceId,
    activeBucket,
    onBucketSelect,
    searchQuery,
    // Reports the active bucket's { label, color } up for the TableShell trace.
    onActiveWidgetChange,
}) => {
    const { newLeadTaskCounts, isLoadingNewLeadTaskCounts } = useSelector(
        (s) => s.widgetCounter,
    );
    const searchActive = !!(searchQuery && searchQuery.trim());

    // Generation counter to drop stale responses on rapid practice switches.
    const generationRef = useRef(0);

    // Track locally which card is highlighted (mirrors activeBucket prop on
    // first render; parent keeps the true source of truth).
    const [activeKey, setActiveKey] = useState(activeBucket || null);

    // Sync local highlight when parent resets (e.g. on strip-tab deselect).
    useEffect(() => {
        setActiveKey(activeBucket || null);
    }, [activeBucket]);

    // Fetch counts on mount and whenever practiceId or searchQuery changes.
    // When a search is active, pass the query so the BE scopes bucket counts
    // to matching leads only (informational match-breakdown, not a filter).
    // setActiveKey(null) is NOT called here: on practiceId change the parent
    // resets taskDueBucket → activeBucket prop → the sync effect above clears
    // activeKey. On search change only the counts re-fetch; cards become
    // display-only because the table intentionally shows all matching search
    // results rather than applying task_due_bucket.
    useEffect(() => {
        if (!practiceId) return undefined;
        generationRef.current += 1;
        dispatch(clearNewLeadTaskCounts());
        const thunk = getNewLeadTaskWidgetCounts(practiceId, searchQuery || null, generationRef);
        dispatch(thunk);
        return undefined;
    }, [practiceId, searchQuery]);

    // Generic handler for plain (non-merged-family) cards — currently only 'upcoming'.
    const handleCardClick = useCallback((card) => {
        if (searchActive) return;
        const { key } = card;
        const isCurrentlyActive = activeKey === key;
        const nextKey = isCurrentlyActive ? null : key;
        setActiveKey(nextKey);
        onBucketSelect(nextKey);
    }, [activeKey, onBucketSelect, searchActive]);

    // The real bucket a card BODY click selects. "No active task" uses the backend's union bucket
    // when the counts response advertises it, else the old `no_next_action` (compat helper).
    const bodyBucketFor = (family) => (
        family.cardKey === 'no_active_task' ? resolveNoActiveTaskBucket(newLeadTaskCounts) : family.bodyBucket
    );

    // Chip click on a merged-family card: selects that EXACT bucket, or falls
    // back to the family's body bucket if that chip is already active.
    const handleChipClick = useCallback((family, bucket) => {
        if (searchActive) return;
        const nextKey = resolveFamilyClickTarget(bucket, activeKey, family, false, bodyBucketFor(family));
        setActiveKey(nextKey);
        onBucketSelect(nextKey);
    }, [activeKey, onBucketSelect, searchActive, newLeadTaskCounts]);

    // Body click on a merged-family card: selects the family's body bucket,
    // or clears (null) if any part of the family (body or either chip) is
    // already active.
    const handleFamilyBodyClick = useCallback((family) => {
        if (searchActive) return;
        const nextKey = resolveFamilyClickTarget(family.bodyBucket, activeKey, family, true, bodyBucketFor(family));
        setActiveKey(nextKey);
        onBucketSelect(nextKey);
    }, [activeKey, onBucketSelect, searchActive, newLeadTaskCounts]);

    const handleAnyCardClick = useCallback((card) => {
        const family = MERGED_FAMILIES.find((f) => f.cardKey === card.key);
        if (family) {
            handleFamilyBodyClick(family);
            return;
        }
        handleCardClick(card);
    }, [handleFamilyBodyClick, handleCardClick]);

    const counts = newLeadTaskCounts || {};

    // Search-aware key the ActionCards row (and every chip's `selected`) use
    // for "is this currently the applied filter" — see the ISS-042 note on
    // the trace effect below for why this must be search-aware too, not the
    // raw activeKey.
    const tracedKey = searchActive ? null : activeKey;

    // Backend-capability compat (utils/newLeadDueNowCompat): NEW backend → headline = due_now,
    // chips overdue / due_today as returned (disjoint). OLD backend (no `due_now` key) →
    // due_today is a SUPERSET of overdue, so headline = due_today and the "Due today" chip =
    // due_today − overdue, keeping the chips summing to the headline. The table request is
    // translated to the same rule in getNewLead.
    const dueNowResolved = resolveDueNowCounts(newLeadTaskCounts) || { headline: 0, overdue: 0, dueToday: 0 };
    const dueNowCount = dueNowResolved.headline;
    const dueNowChipCounts = { overdue: dueNowResolved.overdue, due_today: dueNowResolved.dueToday };
    const noActiveTaskCount = resolveNoActiveTaskCount(newLeadTaskCounts);

    // Grey "N new leads" total — pinned to the far right of the "Tasks due" chips row (CHIP_GREY
    // tone; the tone chips keep green→amber→red). Unscoped (no bucket, no search) = selected;
    // clicking while a bucket is active clears it (unscoped list), while unscoped does nothing.
    const newLeadTotal = resolveNewLeadTotal(newLeadTaskCounts);
    const isUnscoped = !tracedKey && !searchActive;
    const clearBucket = () => {
        if (searchActive || !activeKey) return;
        setActiveKey(null);
        onBucketSelect(null);
    };
    // Emphasis: while the grey total is the active selection, the card headline and the tone chips
    // drop to 0.45 so "62 new leads" reads as the figure in force; selecting a bucket restores them.
    const fadeDueNow = isUnscoped && newLeadTotal !== null;
    const leadWord = (n) => (n === 1 ? 'lead' : 'leads');
    const totalChip = newLeadTotal === null ? null : buildTotalChip({
        testId: 'new-lead-total-badge',
        text: `${newLeadTotal.toLocaleString()} new ${leadWord(newLeadTotal)}`,
        tooltip: `${newLeadTotal.toLocaleString()} new ${leadWord(newLeadTotal)} in total — click to show all`,
        selected: isUnscoped,
        onClick: clearBucket,
    });

    const cards = [
        {
            key: BUCKET.DUE_NOW,
            count: dueNowCount,
            label: BUCKET_LABELS[BUCKET.DUE_NOW],
            icon: BUCKET_ICONS[BUCKET.DUE_NOW],
            urgencyState: getUrgency(BUCKET.DUE_NOW, dueNowCount),
            // Task O: New-lead stage accent (blue) — see actionCardColors.js.
            accentColor: STAGE_ACCENT_COLORS.NEW,
            tooltip: searchActive ? `${BUCKET_TOOLTIPS[BUCKET.DUE_NOW]} Search is active, so this card is a breakdown, not a table filter.` : BUCKET_TOOLTIPS[BUCKET.DUE_NOW],
            nonInteractive: searchActive,
            fadeEmphasis: fadeDueNow,
            totalChip,
            headlineTooltip: `${dueNowCount} new ${leadWord(dueNowCount)} ${dueNowCount === 1 ? 'has' : 'have'} a task due today or earlier`,
            isV2Split: true,
            splitChips: MERGED_FAMILIES[0].chips.map((chip) => ({
                bucket: chip.bucket,
                text: `${dueNowChipCounts[chip.bucket] || 0} ${chip.label}`,
                type: chip.type,
                dotColor: chip.dotColor,
                tooltip: chip.bucket === BUCKET.OVERDUE
                    ? `${dueNowChipCounts[chip.bucket] || 0} new ${leadWord(dueNowChipCounts[chip.bucket] || 0)} with an overdue task`
                    : `${dueNowChipCounts[chip.bucket] || 0} new ${leadWord(dueNowChipCounts[chip.bucket] || 0)} with a task due today`,
                onClick: searchActive ? undefined : () => handleChipClick(MERGED_FAMILIES[0], chip.bucket),
                selected: tracedKey === chip.bucket,
            })),
        },
        {
            key: BUCKET.UPCOMING,
            count: counts.upcoming || 0,
            label: BUCKET_LABELS[BUCKET.UPCOMING],
            icon: BUCKET_ICONS[BUCKET.UPCOMING],
            urgencyState: getUrgency(BUCKET.UPCOMING, counts.upcoming || 0),
            tooltip: searchActive ? `${BUCKET_TOOLTIPS[BUCKET.UPCOMING]} Search is active, so this card is a breakdown, not a table filter.` : BUCKET_TOOLTIPS[BUCKET.UPCOMING],
            nonInteractive: searchActive,
        },
        // Task K: "Checklist Complete" + "No Tasks" merged into one card —
        // combined count as the headline number, two clickable chips for the
        // individual buckets.
        {
            key: MERGED_FAMILIES[1].cardKey,
            count: noActiveTaskCount,
            label: MERGED_FAMILIES[1].cardLabel,
            icon: 'mdi:calendar-remove-outline',
            // Task O (part 2): RED is reserved for "Overdue" — this card's headline no
            // longer turns red/green with count, it takes the same New-stage (blue)
            // accent as its "Tasks due" sibling, always plain dark text.
            urgencyState: URGENCY_STATES.NEUTRAL,
            accentColor: STAGE_ACCENT_COLORS.NEW,
            tooltip: searchActive ? `${NO_ACTIVE_TASK_TOOLTIP} Search is active, so this card is a breakdown, not a table filter.` : NO_ACTIVE_TASK_TOOLTIP,
            nonInteractive: searchActive,
            isV2Split: true,
            splitChips: MERGED_FAMILIES[1].chips.map((chip) => ({
                bucket: chip.bucket,
                text: `${counts[chip.bucket] || 0} ${chip.label}`,
                type: chip.type,
                dotColor: chip.dotColor,
                onClick: searchActive ? undefined : () => handleChipClick(MERGED_FAMILIES[1], chip.bucket),
                selected: tracedKey === chip.bucket,
            })),
        },
    ];

    // Report the active bucket up for the TableShell trace (all buckets filter, so
    // no exclusions). Matches the highlighted card's label + urgency colour.
    // ISS-042: read the SAME search-aware key the ActionCards row receives below, not the
    // raw activeKey. With a search active the bucket filter is lifted (coreThunks gates the
    // param on `taskDueBucket && !hasSearch`) and no card renders selected — but the trace
    // was still reporting activeKey, so it named a bucket that was not applied.
    useEffect(() => {
        if (!onActiveWidgetChange) return;
        if (!tracedKey) {
            // Unscoped list → the strip names it "All new leads" (null under an active search).
            onActiveWidgetChange(searchActive ? null : { label: ALL_NEW_LABEL, color: urgencyColor(URGENCY_STATES.NEUTRAL) });
            return;
        }
        const family = MERGED_FAMILIES.find((f) => isFamilyActive(tracedKey, f));
        if (family) {
            // Name the specific chip, e.g. "Tasks due · Overdue" — the card's
            // own `label` alone would lose which bucket applies. When the
            // card BODY itself is the active key (due_now — not one of its
            // chip buckets), there's no refinement to name.
            const chip = family.chips.find((c) => c.bucket === tracedKey);
            onActiveWidgetChange({
                label: chip ? `${family.cardLabel} · ${chip.label}` : family.cardLabel,
                color: urgencyColor(getUrgency(tracedKey, counts[tracedKey] || 0)),
            });
            return;
        }
        const activeCard = cards.find((c) => c.key === tracedKey);
        if (!activeCard) {
            onActiveWidgetChange(null);
            return;
        }
        onActiveWidgetChange({ label: activeCard.label, color: urgencyColor(activeCard.urgencyState) });
    }, [tracedKey, searchActive, dueNowCount, counts.due_now, counts.due_today, counts.overdue, counts.upcoming, counts.checklist_complete, counts.no_next_action, onActiveWidgetChange]);

    // Clear the trace when this widget unmounts.
    useEffect(() => () => onActiveWidgetChange && onActiveWidgetChange(null), []);

    const selectedKeyForCards = resolveSelectedCardKey(tracedKey);

    return (
        <ActionCards
            cards={cards}
            onCardClick={handleAnyCardClick}
            sortByUrgency={false}
            selectedKey={selectedKeyForCards}
            loading={isLoadingNewLeadTaskCounts}
            loadedKeys={newLeadTaskCounts ? cards.map((c) => c.key) : []}
            skeletonCount={3}
        />
    );
};

NewLeadTaskDueWidget.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    activeBucket: PropTypes.string,
    onBucketSelect: PropTypes.func.isRequired,
    searchQuery: PropTypes.string,
    onActiveWidgetChange: PropTypes.func,
};

NewLeadTaskDueWidget.defaultProps = {
    activeBucket: null,
    searchQuery: null,
};

export default NewLeadTaskDueWidget;
